import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Globe, Lock, Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PrivacyIndicatorProps {
  privacy: 'public' | 'private';
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  className?: string;
}

export function PrivacyIndicator({ 
  privacy, 
  size = 'md', 
  showText = true,
  className 
}: PrivacyIndicatorProps) {
  const isPublic = privacy === 'public';
  
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-xs px-2 py-1',
    lg: 'text-sm px-2.5 py-1'
  };

  const iconSize = {
    sm: 'w-2.5 h-2.5',
    md: 'w-3 h-3',
    lg: 'w-3.5 h-3.5'
  }[size];

  return (
    <Badge 
      variant={isPublic ? 'default' : 'secondary'}
      className={cn(
        sizeClasses[size],
        'inline-flex items-center gap-1',
        isPublic ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700 text-white',
        className
      )}
    >
      {isPublic ? (
        <Globe className={iconSize} />
      ) : (
        <Lock className={iconSize} />
      )}
      {showText && (
        <span>{isPublic ? 'Public' : 'Private'}</span>
      )}
    </Badge>
  );
}

interface PrivacyToggleProps {
  privacy: 'public' | 'private';
  onPrivacyChange: (privacy: 'public' | 'private') => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function PrivacyToggle({
  privacy,
  onPrivacyChange,
  disabled = false,
  size = 'md',
  className
}: PrivacyToggleProps) {
  const isPublic = privacy === 'public';
  
  const buttonClasses = {
    sm: 'h-7 px-2 text-xs',
    md: 'h-8 px-3 text-sm',
    lg: 'h-9 px-4 text-sm'
  };

  const iconSize = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4'
  }[size];

  return (
    <button
      type="button"
      onClick={() => onPrivacyChange(isPublic ? 'private' : 'public')}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        buttonClasses[size],
        isPublic 
          ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
          : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100',
        className
      )}
      title={`Click to make ${isPublic ? 'private' : 'public'}`}
    >
      {isPublic ? (
        <>
          <Eye className={iconSize} />
          Public
        </>
      ) : (
        <>
          <EyeOff className={iconSize} />
          Private
        </>
      )}
    </button>
  );
}

interface PrivacyDescriptionProps {
  privacy: 'public' | 'private';
  context?: 'rating' | 'review' | 'shelf' | 'profile';
  className?: string;
}

export function PrivacyDescription({ 
  privacy, 
  context = 'rating',
  className 
}: PrivacyDescriptionProps) {
  const isPublic = privacy === 'public';
  
  const descriptions = {
    rating: {
      public: 'Visible on your profile and helps other readers discover books',
      private: 'Only visible to you and helps improve your recommendations'
    },
    review: {
      public: 'Others can read your review on your profile and book pages',
      private: 'Only you can see this review - it won\'t appear publicly'
    },
    shelf: {
      public: 'Others can see which books you\'re reading or want to read',
      private: 'Your reading lists are private and only visible to you'
    },
    profile: {
      public: 'Your profile can be found and viewed by anyone',
      private: 'Only people you approve can see your profile and activity'
    }
  };

  return (
    <p className={cn(
      'text-xs flex items-center gap-1.5',
      isPublic ? 'text-green-700' : 'text-blue-700',
      className
    )}>
      {isPublic ? (
        <Globe className="w-3 h-3 flex-shrink-0" />
      ) : (
        <Lock className="w-3 h-3 flex-shrink-0" />
      )}
      <span>{descriptions[context][privacy]}</span>
    </p>
  );
}